import React, { useState } from 'react';
import { 
  FileUp, 
  FileText, 
  Table as TableIcon, 
  Download, 
  RefreshCw, 
  CheckCircle2, 
  AlertCircle,
  User,
  CreditCard,
  Briefcase,
  Calendar,
  GraduationCap,
  ArrowRight,
  Sparkles
} from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { extractArrearData, ExtractedArrearData } from '../src/utils/arrearEngine';
import { jsPDF } from 'jspdf';
import 'jspdf-autotable';
import { addWatermarkToPdf } from '../lib/pdfUtils';
import { LoadingOverlay } from './LoadingOverlay';

export const ArrearAutomaticView: React.FC = () => {
  const [isProcessing, setIsProcessing] = useState(false);
  const [isPaymentProcessing, setIsPaymentProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<ExtractedArrearData | null>(null);
  const [profile, setProfile] = useState({ name: '', pran: '', designation: '' });
  const [joiningDate, setJoiningDate] = useState('');
  const [financialYear, setFinancialYear] = useState('2025-26');

  // Re-calculate MA if joiningDate changes after extraction
  React.useEffect(() => {
    if (data && joiningDate) {
      const updatedMonthlyDrawn = data.monthlyDrawn.map(m => {
        let ma = m.ma;
        if (m.pay > 0) {
          try {
            const jDate = new Date(joiningDate);
            if (!isNaN(jDate.getTime())) {
              const jMonth = jDate.getMonth();
              const jYear = jDate.getFullYear();
              const jDay = jDate.getDate();

              const monthNames = [
                "January", "February", "March", "April", "May", "June", 
                "July", "August", "September", "October", "November", "December"
              ];
              const currentMonthIndex = monthNames.indexOf(m.month);
              
              const [startYearStr, endYearStr] = financialYear.split('-');
              const startYear = parseInt(startYearStr);
              const endYear = 2000 + parseInt(endYearStr);
              const currentYear = (currentMonthIndex >= 2) ? startYear : endYear;

              if (currentYear === jYear && currentMonthIndex === jMonth) {
                const daysInMonth = new Date(currentYear, currentMonthIndex + 1, 0).getDate();
                const daysWorked = daysInMonth - jDay + 1;
                ma = Math.round((1000 / daysInMonth) * daysWorked);
              } else if (currentYear > jYear || (currentYear === jYear && currentMonthIndex > jMonth)) {
                ma = 1000;
              } else {
                ma = 0;
              }
            }
          } catch (e) {
            console.error("Error re-calculating MA:", e);
          }
        } else {
          ma = 0;
        }
        const gis = m.pay > 0 ? 30 : 0;
        return { ...m, ma, gis };
      });

      // Only update if there's a change to avoid infinite loop
      const hasChanged = JSON.stringify(updatedMonthlyDrawn) !== JSON.stringify(data.monthlyDrawn);
      if (hasChanged) {
        setData({ ...data, monthlyDrawn: updatedMonthlyDrawn });
      }
    }
  }, [joiningDate, financialYear]);

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (file.type !== 'application/pdf') {
      setError('Please upload a valid PDF file.');
      return;
    }

    setIsProcessing(true);
    setError(null);

    try {
      const extracted = await extractArrearData(file, false, joiningDate, financialYear);
      setData(extracted);
      setProfile({
        name: extracted.profile.name,
        pran: extracted.profile.pran,
        designation: "done" // Defaulting to "done" as per existing logic
      });
      setIsProcessing(false);
    } catch (err: any) {
      setError(err.message || 'Error processing PDF.');
      setIsProcessing(false);
    }
  };

  const handlePayment = async () => {
    try {
      setIsPaymentProcessing(true);
      setError(null);

      // 1. Create order
      const orderResponse = await fetch('/api/create-razorpay-order', { method: 'POST' });
      const orderData = await orderResponse.json();

      if (!orderData.success) {
        throw new Error('Failed to create payment order');
      }

      // 2. Configure Razorpay
      const options = {
        key: import.meta.env.VITE_RAZORPAY_KEY_ID || "",
        amount: orderData.order.amount,
        currency: "INR",
        name: "BR Teacher Portal",
        description: "Arrear PDF Download",
        order_id: orderData.order.id,
        handler: async function (response: any) {
          try {
            // 3. Verify payment
            const verifyRes = await fetch('/api/verify-razorpay-payment', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify(response)
            });
            const verifyData = await verifyRes.json();
            
            if (verifyData.success) {
              // 4. Generate PDF
              generatePDF();
            } else {
              setError("Payment verification failed!");
            }
          } catch (err) {
            console.error("Verification error:", err);
            setError("Payment verification error.");
          } finally {
            setIsPaymentProcessing(false);
          }
        },
        modal: {
          ondismiss: function() {
            setIsPaymentProcessing(false);
          }
        },
        theme: { color: "#ea580c" }
      };

      // 5. Open Razorpay popup
      const rzp = new (window as any).Razorpay(options);
      rzp.on('payment.failed', function (response: any) {
        console.error("Payment failed:", response.error);
        setError("Payment failed: " + response.error.description);
        setIsPaymentProcessing(false);
      });
      rzp.open();

    } catch (err: any) {
      console.error("Payment error:", err);
      setError(err.message || "Something went wrong with the payment gateway.");
      setIsPaymentProcessing(false);
    }
  };

  const generatePDF = () => {
    if (!data) return;
    const doc = new jsPDF();
    
    doc.setFontSize(18);
    doc.text('Bihar Teacher Help - Arrear Automatic Section', 14, 20);
    
    let rawDesignation = profile.designation || '';
    let extractedName = '';
    let classRange = '';
    
    // Extract name and class range from designation if needed
    if (rawDesignation.includes("EMPLOYEE NAME")) {
      const parts = rawDesignation.split(/EMPLOYEE NAME\s*[:\-]?/i);
      if (parts.length > 1) {
        rawDesignation = parts[0].trim();
        extractedName = parts[1].trim();
      }
    }

    // Extract PRAN if it's accidentally in the designation field
    let extractedPran = '';
    if (rawDesignation.includes("PRAN NO")) {
      const parts = rawDesignation.split(/PRAN NO\s*[:\-]?/i);
      if (parts.length > 1) {
        rawDesignation = parts[0].trim();
        const pranMatch = parts[1].trim().match(/^\d+/);
        if (pranMatch) extractedPran = pranMatch[0];
      }
    }

    // Extract class range like (11-12) or (9-10)
    const classMatch = rawDesignation.match(/\(?(\d+-\d+)\)?/);
    if (classMatch) {
      classRange = classMatch[1];
      rawDesignation = rawDesignation.replace(classMatch[0], '').trim();
    }
    
    // Format classRange to "1-5(School Teacher)"
    let formattedClass = classRange || data.profile.classRange.replace('CLASS :- ', '');
    if (formattedClass && !formattedClass.includes("School Teacher")) {
      formattedClass = `${formattedClass}(School Teacher)`;
    }
    
    const finalName = (profile.name || extractedName).toUpperCase();
    
    doc.setFontSize(12);
    doc.text(`NAME :- ${finalName}`, 14, 35);
    doc.text(profile.pran || extractedPran, 14, 42);
    doc.text(`DESIGNATION / CLASS :- ${profile.designation.toUpperCase()}`, 14, 49);

    const tableData = data.monthlyDrawn.map((d: any) => [
      d.month,
      d.pay,
      d.da,
      d.hra,
      d.pay + d.da + d.hra,
      d.nps
    ]);

    (doc as any).autoTable({
      startY: 65,
      head: [['Month', 'Basic Pay', 'D.A.', 'H.R.A.', 'Total', 'NPS (10%)']],
      body: tableData,
      theme: 'grid',
      headStyles: { fillColor: [20, 20, 20] }
    });

    addWatermarkToPdf(doc);

    doc.save(`Arrear_${data.profile.name.toUpperCase()}.pdf`);
  };

  return (
    <>
    <LoadingOverlay isLoading={isProcessing || isPaymentProcessing} message={isProcessing ? "Extracting Data..." : "Processing Payment..."} />
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 md:py-12">
      <AnimatePresence mode="wait">
        {!data ? (
          <motion.div 
            key="upload"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="max-w-2xl mx-auto"
          >
            <div className="bg-white rounded-2xl md:rounded-[2.5rem] border border-[#141414]/5 p-5 md:p-12 text-center shadow-2xl shadow-black/5">
              <div className="w-14 h-14 md:w-24 md:h-24 bg-[#141414] rounded-xl md:rounded-[2rem] flex items-center justify-center mx-auto mb-4 md:mb-8 text-white shadow-xl shadow-black/20">
                <FileUp size={28} className="md:hidden" />
                <FileUp size={40} className="hidden md:block" />
              </div>
              <h2 className="text-xl md:text-4xl font-black mb-2 md:mb-4 tracking-tight">ARREAR AUTOMATIC CALCULATOR</h2>
              <p className="text-[#141414]/60 mb-6 md:mb-10 leading-relaxed text-sm md:text-lg">
                Extract financial data from Bihar HRMS Yearly Salary Slips with 100% precision.
              </p>
              
              <div className="space-y-2.5 md:space-y-4 mb-6 md:mb-10 text-left bg-[#F5F5F0] p-4 md:p-6 rounded-xl md:rounded-2xl border border-[#141414]/5">
                <h3 className="text-[8px] md:text-xs font-bold uppercase tracking-widest opacity-40 mb-2 md:mb-4">Extraction Strategy</h3>
                <div className="flex items-start gap-2 md:gap-3 text-[10px] md:text-sm">
                  <CheckCircle2 size={14} className="text-emerald-500 mt-0.5 md:hidden" />
                  <CheckCircle2 size={18} className="text-emerald-500 mt-0.5 hidden md:block" />
                  <span>Primary Source: Page 2 extraction only.</span>
                </div>
                <div className="flex items-start gap-2 md:gap-3 text-[10px] md:text-sm">
                  <CheckCircle2 size={14} className="text-emerald-500 mt-0.5 md:hidden" />
                  <CheckCircle2 size={18} className="text-emerald-500 mt-0.5 hidden md:block" />
                  <span>Key Phrase: "Dearness Allowance" exact matching.</span>
                </div>
                <div className="flex items-start gap-2 md:gap-3 text-[10px] md:text-sm">
                  <CheckCircle2 size={14} className="text-emerald-500 mt-0.5 md:hidden" />
                  <CheckCircle2 size={18} className="text-emerald-500 mt-0.5 hidden md:block" />
                  <span>Rounding: Basic Pay to nearest 10 (Dahai).</span>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6 md:mb-10 max-w-2xl mx-auto">
                <div className="text-left">
                  <label className="text-[10px] font-black text-orange-600 uppercase tracking-widest ml-1">Joining Date</label>
                  <input 
                    type="date"
                    value={joiningDate}
                    onChange={(e) => setJoiningDate(e.target.value)}
                    className="w-full px-5 py-3 bg-[#F5F5F0] border-2 border-[#141414]/5 rounded-xl font-bold text-[#141414] focus:border-[#141414] outline-none transition-all"
                  />
                </div>
                <div className="text-left">
                  <label className="text-[10px] font-black text-orange-600 uppercase tracking-widest ml-1">Financial Year</label>
                  <select 
                    value={financialYear}
                    onChange={(e) => setFinancialYear(e.target.value)}
                    className="w-full px-5 py-3 bg-[#F5F5F0] border-2 border-[#141414]/5 rounded-xl font-bold text-[#141414] focus:border-[#141414] outline-none transition-all"
                  >
                    <option value="2024-25">2024-25</option>
                    <option value="2025-26">2025-26</option>
                    <option value="2026-27">2026-27</option>
                  </select>
                </div>
              </div>

              <label className="relative group cursor-pointer inline-block w-full">
                <input 
                  type="file" 
                  accept=".pdf" 
                  onChange={handleFileUpload}
                  className="hidden"
                  disabled={isProcessing}
                />
                <div className={`
                  w-full py-3.5 md:py-6 rounded-xl md:rounded-2xl font-black text-base md:text-xl transition-all flex items-center justify-center gap-2.5 md:gap-4
                  ${isProcessing 
                    ? 'bg-[#F5F5F0] text-[#141414]/30 cursor-not-allowed' 
                    : 'bg-[#141414] text-[#F5F5F0] hover:scale-[1.02] hover:shadow-2xl hover:shadow-black/20 active:scale-95'
                  }
                `}>
                  {isProcessing ? (
                    <>
                      <RefreshCw size={20} className="animate-spin md:hidden" />
                      <RefreshCw size={28} className="animate-spin hidden md:block" />
                      EXTRACTING DATA...
                    </>
                  ) : (
                    <>
                      <Sparkles size={20} className="md:hidden" />
                      <Sparkles size={28} className="hidden md:block" />
                      UPLOAD YEARLY SLIP
                    </>
                  )}
                </div>
              </label>

              {error && (
                <motion.div 
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="mt-6 md:mt-8 p-4 bg-red-50 text-red-600 rounded-xl flex items-center gap-3 justify-center border border-red-100"
                >
                  <AlertCircle size={20} />
                  <span className="font-bold text-sm md:text-base">{error}</span>
                </motion.div>
              )}
            </div>
          </motion.div>
        ) : (
          <motion.div 
            key="results"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="space-y-8"
          >
            {/* Profile Section */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 md:gap-6">
              {[
                { 
                  icon: <User size={16} />, 
                  iconLarge: <User size={20} />, 
                  label: "Teacher Name", 
                  value: profile.name, 
                  key: 'name',
                  color: "bg-blue-50 text-blue-600" 
                },
                { 
                  icon: <CreditCard size={16} />, 
                  iconLarge: <CreditCard size={20} />, 
                  label: "PRAN Number", 
                  value: profile.pran, 
                  key: 'pran',
                  color: "bg-purple-50 text-purple-600" 
                },
                { 
                  icon: <Briefcase size={16} />, 
                  iconLarge: <Briefcase size={20} />, 
                  label: "Designation / Class", 
                  value: profile.designation, 
                  key: 'designation',
                  color: "bg-emerald-50 text-emerald-600" 
                },
                { 
                  icon: <Calendar size={16} />, 
                  iconLarge: <Calendar size={20} />, 
                  label: "Joining Date", 
                  value: joiningDate, 
                  key: 'joiningDate',
                  type: 'date',
                  color: "bg-orange-50 text-orange-600" 
                }
              ].map((item, i) => (
                <div key={i} className={`bg-white p-5 md:p-8 rounded-xl md:rounded-[2rem] border border-[#141414]/5 shadow-xl shadow-black/5 ${i === 2 ? 'sm:col-span-2 lg:col-span-1' : ''}`}>
                  <div className={`w-7 h-7 md:w-10 md:h-10 ${item.color} rounded-lg md:rounded-xl flex items-center justify-center mb-2 md:mb-4`}>
                    <div className="md:hidden">{item.icon}</div>
                    <div className="hidden md:block">{item.iconLarge}</div>
                  </div>
                  <div className="text-[8px] md:text-[10px] font-bold uppercase tracking-widest opacity-40 mb-0.5 md:mb-1">{item.label}</div>
                  <input 
                    type={item.type || "text"}
                    value={item.value}
                    onChange={(e) => {
                      if (item.key === 'joiningDate') {
                        setJoiningDate(e.target.value);
                      } else {
                        setProfile(prev => ({ ...prev, [item.key]: e.target.value }));
                      }
                    }}
                    className="w-full font-black text-sm md:text-lg leading-tight bg-transparent border-none focus:ring-0 p-0 outline-none"
                    placeholder={`Enter ${item.label}`}
                  />
                </div>
              ))}
            </div>

            {/* Table Section */}
            <div className="bg-white rounded-2xl md:rounded-[2.5rem] border border-[#141414]/5 shadow-2xl shadow-black/5 overflow-hidden">
              <div className="p-5 md:p-10 border-b border-[#141414]/5 flex flex-col md:flex-row items-center justify-between bg-[#F5F5F0]/30 gap-4 md:gap-6">
                <div className="flex items-center gap-3 md:gap-4 w-full md:w-auto">
                  <div className="bg-emerald-100 p-2 md:p-3 rounded-xl md:rounded-2xl text-emerald-600">
                    <FileText size={20} className="md:hidden" />
                    <FileText size={32} className="hidden md:block" />
                  </div>
                  <div>
                    <h2 className="text-lg md:text-3xl font-black flex items-center gap-2 md:gap-4 tracking-tight">
                      Data Extraction Preview
                    </h2>
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-0.5 md:mt-1">
                      <p className="text-[8px] md:text-sm font-bold opacity-40 uppercase tracking-widest">Extracted from Page 2 of Yearly Salary Slip</p>
                      <span className="hidden md:inline text-gray-300">•</span>
                      <span className="text-[8px] md:text-[10px] font-black text-blue-600 uppercase tracking-widest bg-blue-50 px-2 py-0.5 rounded-md border border-blue-100">Method: Frontend Regex (Fast)</span>
                    </div>
                  </div>
                </div>
                <div className="flex flex-col gap-2 w-full md:w-auto">
                  <div className="flex w-full md:w-auto gap-2 md:gap-4">
                    <button 
                      onClick={() => { setData(null); }}
                      className="flex-1 md:flex-none bg-[#F5F5F0] text-[#141414] px-3 md:px-6 py-2 md:py-3 rounded-lg md:rounded-xl font-bold text-[10px] md:text-sm hover:bg-[#141414] hover:text-[#F5F5F0] transition-all flex items-center justify-center gap-1.5 md:gap-2"
                    >
                      <RefreshCw size={14} className="md:hidden" />
                      <RefreshCw size={18} className="hidden md:block" />
                      RE-UPLOAD
                    </button>
                    <button 
                      onClick={generatePDF}
                      className="flex-1 md:flex-none bg-[#141414] text-[#F5F5F0] px-4 md:px-8 py-2 md:py-3 rounded-lg md:rounded-xl font-bold text-[10px] md:text-sm hover:opacity-90 transition-all flex items-center justify-center gap-1.5 md:gap-2 shadow-xl shadow-black/10"
                    >
                      <Download size={14} className="md:hidden" />
                      <Download size={18} className="hidden md:block" />
                      GENERATE ARREAR PDF
                    </button>
                  </div>
                  <button 
                    onClick={handlePayment}
                    disabled={isPaymentProcessing}
                    className="w-full bg-orange-600 text-white px-4 py-2 rounded-lg font-bold text-[10px] md:text-sm hover:bg-orange-700 transition-all flex items-center justify-center gap-2 shadow-lg disabled:opacity-50"
                  >
                    {isPaymentProcessing ? (
                      <RefreshCw size={14} className="animate-spin" />
                    ) : (
                      <CreditCard size={14} />
                    )}
                    {isPaymentProcessing ? 'PROCESSING...' : 'PAYMENT'}
                  </button>
                </div>
              </div>

              <div className="p-4 md:p-6 bg-blue-50/50 border-b border-blue-100 flex flex-col sm:flex-row items-center sm:items-start text-center sm:text-left gap-2 md:gap-4">
                <div className="bg-blue-100 p-1.5 md:p-2 rounded-lg text-blue-600 shrink-0">
                  <Sparkles size={16} className="md:hidden" />
                  <Sparkles size={20} className="hidden md:block" />
                </div>
                <div className="space-y-1 md:space-y-2">
                  <p className="text-[10px] md:text-sm font-bold text-blue-900 leading-relaxed">
                    <span className="font-black uppercase tracking-widest block mb-0.5 md:mb-1 text-[8px] md:text-xs">Smart Extraction Active</span>
                    The system has automatically extracted these values. They will be mapped to the "Drawn Amount" section in the generated PDF.
                  </p>
                  <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-[8px] md:text-[10px] text-blue-700/60 font-bold uppercase tracking-widest">
                    <span className="flex items-center gap-1"><CheckCircle2 size={10} /> Name: Regex Pattern</span>
                    <span className="flex items-center gap-1"><CheckCircle2 size={10} /> PRAN: 12-Digit Match</span>
                    <span className="flex items-center gap-1"><CheckCircle2 size={10} /> Desig: Keyword Mapping</span>
                  </div>
                </div>
              </div>

              <div className="overflow-x-auto no-scrollbar scroll-smooth">
                <table className="w-full text-center border-collapse min-w-[600px]">
                  <thead>
                    <tr className="bg-[#141414] text-[#F5F5F0]">
                      <th className="px-4 md:px-10 py-3 md:py-6 text-[8px] md:text-xs font-black uppercase tracking-[0.1em] md:tracking-[0.2em] text-center">Month</th>
                      <th className="px-4 md:px-10 py-3 md:py-6 text-[8px] md:text-xs font-black uppercase tracking-[0.1em] md:tracking-[0.2em] text-center">PAY (Col 6)</th>
                      <th className="px-4 md:px-10 py-3 md:py-6 text-[8px] md:text-xs font-black uppercase tracking-[0.1em] md:tracking-[0.2em] text-center">D.A. (Col 7)</th>
                      <th className="px-4 md:px-10 py-3 md:py-6 text-[8px] md:text-xs font-black uppercase tracking-[0.1em] md:tracking-[0.2em] text-center">H.R.A. (Col 8)</th>
                      <th className="px-4 md:px-10 py-3 md:py-6 text-[8px] md:text-xs font-black uppercase tracking-[0.1em] md:tracking-[0.2em] text-center">Total Drawn</th>
                      <th className="px-4 md:px-10 py-3 md:py-6 text-[8px] md:text-xs font-black uppercase tracking-[0.1em] md:tracking-[0.2em] text-center">NPS (Col 14)</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#141414]/5">
                    {data.monthlyDrawn.map((row: any, i: number) => (
                      <motion.tr 
                        key={i}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: i * 0.05 }}
                        className="hover:bg-[#F5F5F0]/50 transition-colors"
                      >
                        <td className="px-4 md:px-10 py-3 md:py-6 font-black text-[10px] md:text-sm">{row.month.toUpperCase()}</td>
                        <td className="px-4 md:px-10 py-3 md:py-6 font-mono font-bold text-sm md:text-lg text-emerald-600">₹{row.pay.toLocaleString()}</td>
                        <td className="px-4 md:px-10 py-3 md:py-6 font-mono font-bold text-sm md:text-lg text-slate-600">₹{row.da.toLocaleString()}</td>
                        <td className="px-4 md:px-10 py-3 md:py-6 font-mono font-bold text-sm md:text-lg text-slate-600">₹{row.hra.toLocaleString()}</td>
                        <td className="px-4 md:px-10 py-3 md:py-6 font-mono font-black text-base md:text-xl text-[#141414]">
                          ₹{(row.pay + row.da + row.hra).toLocaleString()}
                        </td>
                        <td className="px-4 md:px-10 py-3 md:py-6">
                          <span className="bg-[#141414] text-[#F5F5F0] px-2 md:px-4 py-1 md:py-1.5 rounded-lg text-[10px] md:text-sm font-black font-mono">
                            ₹{row.nps.toLocaleString()}
                          </span>
                        </td>
                      </motion.tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="bg-[#F5F5F0] border-t-2 border-[#141414]">
                      <td className="px-4 md:px-10 py-4 md:py-8 font-black text-sm md:text-lg">GRAND TOTAL</td>
                      <td className="px-4 md:px-10 py-4 md:py-8 font-mono font-black text-base md:text-xl text-emerald-600">
                        ₹{data.monthlyDrawn.reduce((acc: number, curr: any) => acc + curr.pay, 0).toLocaleString()}
                      </td>
                      <td className="px-4 md:px-10 py-4 md:py-8 font-mono font-black text-base md:text-xl text-slate-600">
                        ₹{data.monthlyDrawn.reduce((acc: number, curr: any) => acc + curr.da, 0).toLocaleString()}
                      </td>
                      <td className="px-4 md:px-10 py-4 md:py-8 font-mono font-black text-base md:text-xl text-slate-600">
                        ₹{data.monthlyDrawn.reduce((acc: number, curr: any) => acc + curr.hra, 0).toLocaleString()}
                      </td>
                      <td className="px-4 md:px-10 py-4 md:py-8 font-mono font-black text-lg md:text-2xl text-[#141414]">
                        ₹{data.monthlyDrawn.reduce((acc: number, curr: any) => acc + (curr.pay + curr.da + curr.hra), 0).toLocaleString()}
                      </td>
                      <td className="px-4 md:px-10 py-4 md:py-8 font-mono font-black text-base md:text-xl">
                        ₹{data.monthlyDrawn.reduce((acc: number, curr: any) => acc + curr.nps, 0).toLocaleString()}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>

            <div className="flex flex-col md:flex-row items-center justify-center gap-3 md:gap-6 p-5 md:p-8 bg-[#141414] text-[#F5F5F0] rounded-2xl md:rounded-3xl shadow-2xl shadow-black/20">
              <div className="flex items-center gap-2 md:gap-3">
                <CheckCircle2 size={16} className="text-emerald-400 md:hidden" />
                <CheckCircle2 size={24} className="text-emerald-400 hidden md:block" />
                <span className="font-bold text-[10px] md:text-sm tracking-wide">100% PRECISION EXTRACTION</span>
              </div>
              <div className="hidden md:block w-px h-6 bg-white/20" />
              <div className="flex items-center gap-2 md:gap-3">
                <CheckCircle2 size={16} className="text-emerald-400 md:hidden" />
                <CheckCircle2 size={24} className="text-emerald-400 hidden md:block" />
                <span className="font-bold text-[10px] md:text-sm tracking-wide">AUTO ROUNDING APPLIED</span>
              </div>
              <div className="hidden md:block w-px h-6 bg-white/20" />
              <div className="flex items-center gap-2 md:gap-3">
                <CheckCircle2 size={16} className="text-emerald-400 md:hidden" />
                <CheckCircle2 size={24} className="text-emerald-400 hidden md:block" />
                <span className="font-bold text-[10px] md:text-sm tracking-wide">NPS CALCULATION VERIFIED</span>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
    </>
  );
};
